import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import { companyInfo } from '../data/mock';

const Navigation = ({ t, currentLang, onLanguageChange }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')}>
            <img
              src={companyInfo.logo}
              alt="YmaKOOLat"
              className="h-30 w-auto"
              style={{ height: '7.5rem' }}
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-[#2196f3] font-medium transition-colors"
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-[#2196f3] font-medium transition-colors"
            >
              {t.nav.services}
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className="text-gray-700 hover:text-[#2196f3] font-medium transition-colors"
            >
              {t.nav.gallery}
            </button>
            <button
              onClick={() => scrollToSection('reviews')}
              className="text-gray-700 hover:text-[#2196f3] font-medium transition-colors"
            >
              {t.nav.reviews}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-[#2196f3] font-medium transition-colors"
            >
              {t.nav.contact}
            </button>
          </div>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 text-[#0d2e5d] hover:text-[#2196f3] font-medium transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">{companyInfo.phone}</span>
            </a>
            <LanguageSwitcher
              currentLang={currentLang}
              onLanguageChange={onLanguageChange}
            />
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-block"
            >
              <Button className="bg-[#2196f3] hover:bg-[#1976d2] text-white">
                {t.common.contactUs}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageSwitcher
              currentLang={currentLang}
              onLanguageChange={onLanguageChange}
            />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 hover:text-[#2196f3] transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 bg-white/95 backdrop-blur-md border-t">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection('home')}
                className="text-left px-4 py-2 text-gray-700 hover:text-[#2196f3] hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                {t.nav.home}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-left px-4 py-2 text-gray-700 hover:text-[#2196f3] hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                {t.nav.services}
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className="text-left px-4 py-2 text-gray-700 hover:text-[#2196f3] hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                {t.nav.gallery}
              </button>
              <button
                onClick={() => scrollToSection('reviews')}
                className="text-left px-4 py-2 text-gray-700 hover:text-[#2196f3] hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                {t.nav.reviews}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left px-4 py-2 text-gray-700 hover:text-[#2196f3] hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                {t.nav.contact}
              </button>
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-2 px-4 py-2 text-[#0d2e5d] hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>{companyInfo.phone}</span>
              </a>
              <div className="px-4">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="block w-full"
                >
                  <Button className="w-full bg-[#2196f3] hover:bg-[#1976d2] text-white">
                    {t.common.contactUs}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
